import { Heading, Subheading } from "../../../../design/heading";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../firebase";
import { Button } from "../../../../design/button";
import { Text } from "../../../../design/text";
import { Divider } from "../../../../design/divider";
import SettingsHeader from "../SettingsHeader";
import { useState } from "react";
import {
  CreditCardIcon,
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

const BillingPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  const onStripeButtonClicked = async () => {
    setIsLoading(true);
    try {
      const result = await httpsCallable(
        functions,
        "generateBillingPortalSession",
      )({});
      const data = result.data as { url: string };
      window.open(data.url, "_blank");
    } catch (err) {
      console.error("Error generating billing portal session:", err);
      alert(
        "Failed to generate billing portal session. Please try again later.",
      );
    }
    setIsLoading(false);
  };

  return (
    <>
      <SettingsHeader />

      <div className="space-y-8">
        {/* Header */}
        <div>
          <Heading>Billing & Plans</Heading>
          <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
            Manage your subscription, payment methods, and view billing history.
          </Text>
        </div>

        {/* Stripe Billing Portal Card */}
        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <CreditCardIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <Subheading>Stripe Billing Portal</Subheading>
              <Text className="mt-1 text-zinc-600 dark:text-zinc-400">
                Access your Stripe billing portal to manage payment methods, view invoices,
                update your subscription, and download receipts.
              </Text>
              <Button
                className="mt-4"
                color="blue"
                onClick={onStripeButtonClicked}
                disabled={isLoading}
              >
                <span className="flex items-center gap-2">
                  {isLoading ? "Opening..." : "Open Billing Portal"}
                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                </span>
              </Button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="p-4 bg-white border rounded-lg dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                <DocumentTextIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <Text className="font-medium text-zinc-900 dark:text-white">View Invoices</Text>
                <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                  Access past invoices and receipts
                </Text>
              </div>
            </div>
          </div>

          <div className="p-4 bg-white border rounded-lg dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                <ClockIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <Text className="font-medium text-zinc-900 dark:text-white">Payment History</Text>
                <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                  Review your payment history
                </Text>
              </div>
            </div>
          </div>
        </div>

        <Divider />

        {/* Info Section */}
        <div className="p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50">
          <Subheading className="mb-2">About Billing</Subheading>
          <Text className="text-sm text-zinc-600 dark:text-zinc-400">
            VisitProof uses Stripe for secure payment processing. In the billing portal,
            you can update your payment method, cancel or modify your subscription, and
            download invoices for your records.
          </Text>
        </div>
      </div>
    </>
  );
};

export default BillingPage;
